import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, Event, EventResponse } from '../lib/supabase';
import { useEmberStore } from '../lib/store';
import { toast } from '../lib/toast';
import EventAttendeesModal from './EventAttendeesModal';
import { X, MapPin, Clock, User, Users, Check, Edit, Trash2, Navigation } from 'lucide-react';
import { useLeaflet } from '../hooks/useLeaflet';
import LoadingSpinner from './LoadingSpinner';

interface EventPopupProps {
  event: Event;
  onClose: () => void;
  onResponseUpdate?: () => void;
}

export default function EventPopup({ event, onClose, onResponseUpdate }: EventPopupProps) {
  const navigate = useNavigate();
  const { currentUser } = useEmberStore();
  const [loading, setLoading] = useState(false);
  const [userResponse, setUserResponse] = useState<EventResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [attendeeCount, setAttendeeCount] = useState(0);
  const [showAttendeesModal, setShowAttendeesModal] = useState(false);
  const leaflet = useLeaflet();
  const { L, components } = leaflet ?? {};
  const MapContainer = components?.MapContainer;
  const TileLayer = components?.TileLayer;
  const Marker = components?.Marker;

  useEffect(() => {
    if (!L) return;
    delete (L.Icon.Default.prototype as any)._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
    });
  }, [L]);

  const eventMarkerIcon = useMemo(() => {
    if (!L) return null;
    return L.icon({
      iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
  }, [L]);

  // Get user's current response if provided on the event object (Home/Discover).
  const currentResponse = event.responses?.find(r => r.user_id === currentUser?.id);

  // Fetch attendee count when component mounts
  useEffect(() => {
    async function fetchAttendeeCount() {
      try {
        const { data, error } = await supabase.rpc('get_event_attendee_count', {
          event_uuid: event.id
        });

        if (error) throw error;
        setAttendeeCount(data || 0);
      } catch (err) {
        console.error('Error fetching attendee count:', err);
      }
    }

    fetchAttendeeCount();
  }, [event.id]);

  async function handleResponse(responseType: 'attending' | 'interested' | 'not_interested') {
    if (!currentUser) return;

    setLoading(true);
    try {
      // If user already has a response, update it; else insert
      if (currentResponse) {
        const { error } = await supabase
          .from('event_responses')
          .update({ response_type: responseType })
          .eq('id', currentResponse.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('event_responses')
          .insert({ event_id: event.id, user_id: currentUser.id, response_type: responseType });
        if (error) throw error;
      }

      // Refresh data
      if (onResponseUpdate) onResponseUpdate();
      onClose();
    } catch (err) {
      console.error('Error updating event response:', err);
      toast.error('Failed to update response');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    // Styled confirmation modal
    const ok = await new Promise<boolean>((resolve) => {
      const modal = document.createElement('div');
      modal.style.cssText = `position:fixed;inset:0;background:rgba(0,0,0,0.8);display:flex;align-items:center;justify-content:center;z-index:1000;padding:1rem;`;
      const content = document.createElement('div');
      content.style.cssText = `background:#1a1a1a;border:1px solid #e4574d;border-radius:12px;padding:2rem;max-width:400px;width:100%;text-align:center;color:#ffffff;`;
      content.innerHTML = `
        <h3 style="font-size:1.25rem;margin-bottom:1rem;color:#e4574d;">Delete Event</h3>
        <p style="color:#cfcfcf;margin-bottom:1.5rem;">Are you sure you want to delete this event? This action cannot be undone.</p>
        <div style="display:flex;gap:1rem;justify-content:center;">
          <button id="cancel" style="padding:0.75rem 1.5rem;background:transparent;color:#999;border:1px solid #666;border-radius:6px;cursor:pointer;font-weight:600;">Cancel</button>
          <button id="confirm" style="padding:0.75rem 1.5rem;background:#e4574d;color:#fff;border:none;border-radius:6px;cursor:pointer;font-weight:600;">Delete</button>
        </div>
      `;
      modal.appendChild(content);
      document.body.appendChild(modal);
      content.querySelector('#cancel')?.addEventListener('click', () => { modal.remove(); resolve(false); });
      content.querySelector('#confirm')?.addEventListener('click', () => { modal.remove(); resolve(true); });
    });

    if (!ok) return;

    setDeleting(true);
    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', event.id);

      if (error) throw error;
      
      onClose();
      if (onResponseUpdate) onResponseUpdate();
    } catch (err) {
      console.error('Error deleting event:', err);
      toast.error('Failed to delete event');
    } finally {
      setDeleting(false);
    }
  }

  // Check if user is the event creator
  const isCreator = currentUser?.id === event.creator_id;

  const formatDateTime = (dateTime: string) => {
    const date = new Date(dateTime);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const startTime = formatDateTime(event.start_datetime);
  const endTime = event.end_datetime ? formatDateTime(event.end_datetime) : null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1002,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '12px',
        border: '1px solid var(--color-border)',
        maxWidth: '500px',
        width: '100%',
        maxHeight: '85vh',
        overflow: 'auto',
        paddingBottom: '1rem',
        // Use event photo as background when available
        ...(event.photo_url ? {
          backgroundImage: `url(${event.photo_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        } : {})
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: event.photo_url ? 'none' : '1px solid var(--color-border)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          // Add subtle backdrop for readability when background image exists
          ...(event.photo_url ? { backgroundColor: 'rgba(0,0,0,0.45)' } : {})
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#ffffff' }}>
              {event.title}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#cfcfcf' }}>
              <MapPin size={16} />
              <span>{event.venue_name || event.venue?.name || 'Location TBD'}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              padding: '0.25rem'
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem', ...(event.photo_url ? { backgroundColor: 'rgba(0,0,0,0.45)' } : {}) }}>
          {/* Event Details */}
          <div style={{ marginBottom: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.875rem', color: '#cfcfcf' }}>
              <Clock size={16} />
              <span>
                {startTime.date} at {startTime.time}
                {endTime && ` - ${endTime.time}`}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', fontSize: '0.875rem', color: '#cfcfcf' }}>
              <User size={16} />
              <span>by {event.creator?.username}</span>
            </div>

            {/* Attendee Count - Clickable */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem', 
              marginBottom: '0.75rem',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setShowAttendeesModal(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'rgba(196, 255, 0, 0.1)',
                  border: '1px solid rgba(196, 255, 0, 0.3)',
                  borderRadius: '20px',
                  color: 'var(--color-accent)',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(196, 255, 0, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'rgba(196, 255, 0, 0.1)';
                }}
              >
                <Users size={16} />
                <span>{attendeeCount} {attendeeCount === 1 ? 'person' : 'people'} attending</span>
              </button>

              {event.max_attendees && (
                <span style={{ fontSize: '0.875rem', color: '#999' }}>
                  (max {event.max_attendees})
                </span>
              )}
            </div>

            <p style={{ fontSize: '0.875rem', color: '#cfcfcf', lineHeight: '1.6', marginTop: '1rem' }}>
              {event.description}
            </p>
          </div>

          {/* Location Map */}
          {(event.latitude && event.longitude) && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '0.5rem', 
                marginBottom: '0.75rem',
                fontSize: '0.875rem',
                fontWeight: '600',
                color: 'var(--color-accent)'
              }}>
                <MapPin size={16} />
                <span>Event Location</span>
              </div>
              
              <div style={{ 
                height: '200px', 
                borderRadius: '8px', 
                overflow: 'hidden',
                border: '1px solid var(--color-border)',
                marginBottom: '0.75rem'
              }}>
                {MapContainer && TileLayer && Marker && eventMarkerIcon ? (
                  <MapContainer
                    center={[event.latitude, event.longitude]}
                    zoom={15}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={false}
                    dragging={false}
                    scrollWheelZoom={false}
                    doubleClickZoom={false}
                    touchZoom={false}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; OpenStreetMap contributors'
                    />
                    <Marker 
                      position={[event.latitude, event.longitude]}
                      icon={eventMarkerIcon}
                    />
                  </MapContainer>
                ) : (
                  <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
                    <LoadingSpinner text="Loading map..." />
                  </div>
                )}
              </div>
              
              <div style={{ fontSize: '0.75rem', color: '#999', marginBottom: '0.5rem' }}>
                📍 {event.latitude.toFixed(5)}, {event.longitude.toFixed(5)}
              </div>
              
              <a 
                href={`https://www.google.com/maps/dir/?api=1&destination=${event.latitude},${event.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-accent)',
                  borderRadius: '6px',
                  color: 'var(--color-accent)',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                <Navigation size={14} />
                Get Directions
              </a>
            </div>
          )}

          {/* Response Buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <button
              onClick={() => handleResponse('attending')}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: currentResponse?.response_type === 'attending' ? 'var(--color-success)' : 'var(--color-accent-soft)',
                color: currentResponse?.response_type === 'attending' ? '#ffffff' : '#0b0b0b',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                opacity: loading ? 0.5 : 1
              }}
            >
              {currentResponse?.response_type === 'attending' && <Check size={16} />}
              {currentResponse?.response_type === 'attending' ? 'Attending' : 'Attend'}
            </button>

            <button
              onClick={() => handleResponse('interested')}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: currentResponse?.response_type === 'interested' ? 'var(--color-accent)' : 'var(--color-card)',
                color: currentResponse?.response_type === 'interested' ? '#0b0b0b' : 'var(--color-accent)',
                border: `1px solid ${currentResponse?.response_type === 'interested' ? 'var(--color-accent)' : 'var(--color-accent)'}`,
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                opacity: loading ? 0.5 : 1
              }}
            >
              {currentResponse?.response_type === 'interested' ? 'Interested ✓' : 'Interested'}
            </button>

            <button
              onClick={() => handleResponse('not_interested')}
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: 'transparent',
                color: currentResponse?.response_type === 'not_interested' ? 'var(--color-danger)' : '#999',
                border: `1px solid ${currentResponse?.response_type === 'not_interested' ? '#e4574d' : '#666'}`,
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                opacity: loading ? 0.5 : 1
              }}
            >
              {currentResponse?.response_type === 'not_interested' ? 'Not Interested ✓' : 'Not Interested'}
            </button>
          </div>

          {/* Event Creator Actions */}
          {isCreator && (
            <div style={{ 
              marginTop: '1rem', 
              paddingTop: '1rem', 
              borderTop: '1px solid var(--color-border)',
              display: 'flex',
              gap: '0.75rem'
            }}>
              <button
                onClick={() => {
                  onClose();
                  navigate(`/edit-event/${event.id}`);
                }}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-accent)',
                  color: 'var(--color-accent)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600'
                }}
              >
                <Edit size={16} />
                Edit Event
              </button>

              <button
                onClick={handleDelete}
                disabled={deleting}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-danger)',
                color: 'var(--color-danger)',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  opacity: deleting ? 0.5 : 1
                }}
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Event Attendees Modal */}
      <EventAttendeesModal
        eventId={event.id}
        eventTitle={event.title}
        isOpen={showAttendeesModal}
        onClose={() => setShowAttendeesModal(false)}
      />
    </div>
  );
}
